import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  deleteDoc,
  query,
  limit,
  orderBy,
  startAfter,
  onSnapshot,
  Unsubscribe,
  FirestoreDataConverter,
  Timestamp,
} from 'firebase/firestore';
import type { Strategy } from '@segmently/schema-models/strategy';
import type { StepNode } from '@segmently/schema-models/v2';
import { CoreFirebaseService } from './CoreFirebaseService';
import { strategyConverter as clientStrategyConverter } from '../converters/strategyConverter';
import type { OnboardingThemeSettingsV2 } from '../converters/onboardingConverter';
import { StrategyBlockService } from './StrategyBlockService';
import { StrategyStepNodeService } from './StrategyStepNodeService';
import { ProjectThemeService } from './ProjectThemeService';
import { cloneOnboardingThemeSettings } from '@modules/themes/utils';

export interface StrategyStepNodeScreenTarget {
  blockId: string;
  blockName?: string;
  screenId: string;
  screen: StepNode;
}

export { updateTouchesStrategyContent } from '@modules/shared-types/src/strategyHeadWrite';
import { saveStrategyHead, strategyHeadComparableJson } from '@modules/firebase-core/services/StrategyHeadMutationApi';

/**
 * Legacy guard error, kept exported for compatibility — content edits no longer
 * throw it: StrategyService.update auto-reopens a baked/published head as draft
 * (R7/P2 no-dead-end). Server-side agent regeneration still refuses with code
 * STRATEGY_BAKED (AgentController) — that surface is intentionally hard.
 */
export class StrategyBakedError extends Error {
  readonly code = 'STRATEGY_BAKED';
  constructor(
    public readonly strategyId: string,
    public readonly strategyStatus: string,
  ) {
    super(`Strategy ${strategyId} is ${strategyStatus} (read-only). Start a new version to edit.`);
    this.name = 'StrategyBakedError';
  }
}

function stripUndefinedDeep<T>(value: T): T {
  if (value === undefined) {
    return null as T;
  }

  if (Array.isArray(value)) {
    return value.map(item => stripUndefinedDeep(item)) as T;
  }

  if (!value || typeof value !== 'object') {
    return value;
  }

  return Object.entries(value as Record<string, unknown>).reduce<Record<string, unknown>>(
    (result, [key, child]) => {
      if (child !== undefined) {
        result[key] = stripUndefinedDeep(child);
      }
      return result;
    },
    {}
  ) as T;
}

/**
 * StrategyService
 *
 * Type-safe service for Strategy entity CRUD operations and real-time subscriptions.
 *
 * Features:
 * - Full CRUD operations (create, read, update, delete, list)
 * - Cascade delete for blocks and screens subcollections
 * - Cursor-based pagination with 20-item limit
 * - Real-time subscriptions with automatic cleanup
 * - Type-safe: only Strategy types exposed, no Firebase types in public API
 * - Subcollection access via getBlocksService()
 * - Uses strategyConverter for Date↔Timestamp conversion
 *
 * Related Implementation:
 * - Sprint 2, Task T018 (GREEN Phase)
 * - Tests: T013-T017 (RED Phase)
 */
export class StrategyService {
  private readonly projectId: string;
  private readonly firestore = CoreFirebaseService.getInstance().firestore;
  private readonly converter: FirestoreDataConverter<Strategy>;

  /**
   * Create a new StrategyService instance
   *
   * @param projectId - Parent project ID
   * @param converter - Firestore converter for Strategy entity (defaults to client SDK converter)
   */
  constructor(
    projectId: string,
    converter: FirestoreDataConverter<Strategy> = clientStrategyConverter
  ) {
    this.projectId = projectId;
    this.converter = converter;
  }

  /**
   * Get the subcollection path for strategies
   */
  private getCollectionPath(): string {
    return `projects/${this.projectId}/strategies`;
  }

  /**
   * Create a new strategy
   *
   * @param data - Partial strategy data (id will be auto-generated if not provided)
   * @returns {Promise<Strategy>} The created strategy with generated ID
   */
  async create(data: Partial<Strategy>): Promise<Strategy> {
    try {
      const collectionRef = collection(this.firestore, this.getCollectionPath());
      const docRef = doc(collectionRef).withConverter(this.converter);

      const receipt = await saveStrategyHead(this.projectId, docRef.id, {
        mode: 'create', fields: { ...data, id: docRef.id },
      });
      return await this.readVerifiedHead(docRef.id, receipt);
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'StrategyService.create');
      throw error;
    }
  }

  /**
   * Get a strategy by ID
   *
   * @param id - Strategy ID
   * @returns {Promise<Strategy | null>} The strategy or null if not found
   */
  async get(id: string): Promise<Strategy | null> {
    try {
      const docRef = doc(this.firestore, this.getCollectionPath(), id).withConverter(this.converter);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      const data = docSnap.data();
      return { ...data, id: docSnap.id } as Strategy;
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'StrategyService.get');
      throw error;
    }
  }

  /**
   * Update an existing strategy
   *
   * @param id - Strategy ID
   * @param data - Partial strategy data to update
   * @returns {Promise<Strategy>} The updated strategy
   */
  async update(id: string, data: Partial<Strategy>): Promise<Strategy> {
    try {
      const receipt = await saveStrategyHead(this.projectId, id, { mode: 'update', fields: data });
      return await this.readVerifiedHead(id, receipt);
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'StrategyService.update');
      throw error;
    }
  }

  private async readVerifiedHead(id: string, receipt: Record<string, unknown>): Promise<Strategy> {
    const saved = await this.get(id);
    if (!saved || strategyHeadComparableJson(saved) !== strategyHeadComparableJson(receipt)) {
      throw new Error(`Strategy ${id} was saved, but persisted readback changed or could not be verified. Refresh before retrying.`);
    }
    return saved;
  }

  /**
   * Delete a strategy with cascade delete for blocks and screens
   *
   * Deletes:
   * 1. All screens in all blocks (strategies/{id}/blocks/{blockId}/screens)
   * 2. All blocks (strategies/{id}/blocks)
   * 3. The strategy document itself
   * 4. Its V2 theme snapshot (strategies/{id}/themeSettingsV2/settings)
   *
   * (4) is final review Important #2, ruled on ownership: the owning service
   * deletes the WHOLE aggregate. The snapshot is cloned onto every strategy that
   * generates screens (`ensureThemeSettingsV2FromActiveProjectTheme`), and until
   * now no deletion removed it — a document under a deleted parent, invisible to
   * ordinary reads but real to collection-group queries and to cost. It also made
   * wave 9 W1's promise («a refused create leaves no orphan») only half true,
   * because the compensation deletes the strategy through exactly this method.
   *
   * Best effort by construction: `deleteThemeSettingsV2` logs and swallows, so a
   * missing subdoc is not an error and a failure to remove it never turns an
   * already-completed head delete into a thrown error.
   *
   * @param id - Strategy ID
   * @returns {Promise<void>}
   */
  async delete(id: string): Promise<void> {
    try {
      // Step 1: Get all blocks
      const blocksCollectionRef = collection(this.firestore, `${this.getCollectionPath()}/${id}/blocks`);
      const blocksSnapshot = await getDocs(blocksCollectionRef);

      // Step 2: For each block, delete all screens
      for (const blockDoc of blocksSnapshot.docs) {
        const screensCollectionRef = collection(
          this.firestore,
          `${this.getCollectionPath()}/${id}/blocks/${blockDoc.id}/screens`
        );
        const screensSnapshot = await getDocs(screensCollectionRef);

        // Delete all screens
        for (const screenDoc of screensSnapshot.docs) {
          await deleteDoc(screenDoc.ref);
        }

        // Delete the block
        await deleteDoc(blockDoc.ref);
      }

      // Step 3: Delete the strategy document
      const docRef = doc(this.firestore, this.getCollectionPath(), id);
      await deleteDoc(docRef);

      // Step 4: Delete the strategy's own theme snapshot (best effort).
      await this.deleteThemeSettingsV2(id);
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'StrategyService.delete');
      throw error;
    }
  }

  async deleteMany(ids: string[]): Promise<void> {
    const uniqueIds = Array.from(new Set(ids.map((id) => String(id || '').trim()).filter(Boolean)));
    for (const id of uniqueIds) {
      await this.delete(id);
    }
  }

  /**
   * List strategies with cursor-based pagination
   *
   * @param options - Query options
   * @param options.limit - Maximum number of strategies to return (default: 20, null = unbounded)
   * @param options.startAfter - Start after this Date value (for pagination)
   * @returns {Promise<Strategy[]>} Array of strategies
   */
  async list(options: { limit?: number | null; startAfter?: Date | string } = {}): Promise<Strategy[]> {
    try {
      const collectionRef = collection(this.firestore, this.getCollectionPath()).withConverter(this.converter);

      const pageLimit = options.limit === null ? null : (options.limit || 20);

      let q = pageLimit
        ? query(collectionRef, orderBy('createdAt', 'desc'), limit(pageLimit))
        : query(collectionRef, orderBy('createdAt', 'desc'));

      if (options.startAfter) {
        if (!pageLimit) {
          throw new Error('StrategyService.list startAfter requires a positive limit.');
        }
        // Convert ISO string or Date to Firestore Timestamp for cursor
        const cursor = typeof options.startAfter === 'string'
          ? Timestamp.fromDate(new Date(options.startAfter))
          : Timestamp.fromDate(options.startAfter);
        q = query(collectionRef, orderBy('createdAt', 'desc'), startAfter(cursor), limit(pageLimit));
      }

      const querySnapshot = await getDocs(q);

      return querySnapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id
      } as Strategy));
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'StrategyService.list');
      throw error;
    }
  }

  /**
   * Find strategy by name
   *
   * Uses client-side filtering instead of Firestore query to avoid TypeScript import issues.
   * This is acceptable for small datasets (strategies are typically < 100 per project).
   *
   * @param name - Strategy name to search for
   * @returns {Promise<Strategy | null>} The strategy or null if not found
   */
  async findByName(name: string): Promise<Strategy | null> {
    try {
      // Fallback: list all and filter in memory (avoids TypeScript where import issue)
      const strategies = await this.list({ limit: 100 });
      const found = strategies.find(s => s.name === name);
      return found || null;
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'StrategyService.findByName');
      throw error;
    }
  }

  /**
   * Subscribe to real-time updates for a strategy
   *
   * Updates propagate within 500ms (FR-003, FR-015)
   *
   * @param id - Strategy ID
   * @param onNext - Callback when strategy is updated (receives Strategy | null)
   * @param onError - Callback when an error occurs
   * @returns {Unsubscribe} Function to call to unsubscribe
   */
  onSnapshot(
    id: string,
    onNext: (strategy: Strategy | null) => void,
    onError: (error: Error) => void
  ): Unsubscribe {
    try {
      const docRef = doc(this.firestore, this.getCollectionPath(), id).withConverter(this.converter);

      return onSnapshot(
        docRef,
        (snapshot) => {
          if (snapshot.exists()) {
            // Get data through converter
            const data = snapshot.data();
            // Add id field
            const strategy: Strategy = { ...data, id: snapshot.id };
            onNext(strategy);
          } else {
            onNext(null);
          }
        },
        (error) => {
          CoreFirebaseService.getInstance().logError(error, 'StrategyService.onSnapshot');
          onError(error as Error);
        }
      );
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'StrategyService.onSnapshot.setup');
      throw error;
    }
  }

  /**
   * Subscribe to real-time updates for strategies collection
   *
   * Provides real-time updates as strategies are created, updated, or deleted.
   * Updates propagate within 500ms.
   *
   * Similar to list() but with continuous updates instead of one-time fetch.
   *
   * @param onNext - Callback when collection is updated (receives Strategy[])
   * @param onError - Callback when an error occurs
   * @param options - Query options
   * @param options.limit - Maximum number of strategies to return (default: 20)
   * @param options.orderByField - Field to order by (default: 'createdAt')
   * @param options.orderDirection - Sort direction (default: 'desc')
   * @returns {Unsubscribe} Function to call to unsubscribe
   *
   * @example
   * ```typescript
   * const unsubscribe = strategyService.onCollectionSnapshot(
   *   (strategies) => {
   *     console.log('Strategies updated:', strategies);
   *     setStrategies(strategies);
   *   },
   *   (error) => {
   *     console.error('Subscription error:', error);
   *   },
   *   { limit: 50 }
   * );
   *
   * // Later: cleanup
   * unsubscribe();
   * ```
   */
  onCollectionSnapshot(
    onNext: (strategies: Strategy[]) => void,
    onError: (error: Error) => void,
    options: {
      limit?: number | null;
      orderByField?: 'createdAt' | 'updatedAt';
      orderDirection?: 'asc' | 'desc';
    } = {}
  ): Unsubscribe {
    try {
      const collectionRef = collection(this.firestore, this.getCollectionPath()).withConverter(this.converter);

      const pageLimit = options.limit === null ? null : (options.limit || 20);
      const orderField = options.orderByField || 'createdAt';
      const direction = options.orderDirection || 'desc';

      const q = pageLimit
        ? query(collectionRef, orderBy(orderField, direction), limit(pageLimit))
        : query(collectionRef, orderBy(orderField, direction));

      // Subscribe to query snapshots
      return onSnapshot(
        q,
        (snapshot) => {
          // Map documents through converter
          const strategies: Strategy[] = snapshot.docs.map(doc => ({
            ...doc.data(),
            id: doc.id
          } as Strategy));

          onNext(strategies);
        },
        (error) => {
          CoreFirebaseService.getInstance().logError(error, 'StrategyService.onCollectionSnapshot');
          onError(error as Error);
        }
      );
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'StrategyService.onCollectionSnapshot.setup');
      throw error;
    }
  }

  /**
   * Clone a strategy with all its blocks and screens (deep copy)
   *
   * Creates a complete copy of the strategy with:
   * - New strategy ID
   * - " (Copy)" appended to name
   * - All blocks cloned with new IDs
   * - All screens cloned with new IDs
   * - No references to original entities
   *
   * Implementation Notes:
   * - Uses sequential operations for clarity and error handling
   * - System fields (id, createdAt, updatedAt) are regenerated by create()
   * - Empty hierarchies are handled gracefully
   *
   * Performance Considerations:
   * - For strategies with many blocks/screens (>100 items), consider batch operations
   * - Current implementation prioritizes clarity and maintainability
   *
   * Related Implementation:
   * - Sprint 6, Task T043 (GREEN Phase)
   * - Sprint 6, Task T046 (REFACTOR Phase)
   * - Tests: T039, T042 (RED Phase)
   *
   * @param id - Original strategy ID to clone
   * @returns {Promise<Strategy>} The cloned strategy with new ID
   * @throws {Error} If strategy not found
   */
  async cloneStrategy(id: string): Promise<Strategy> {
    try {
      // Step 1: Get original strategy
      const original = await this.get(id);
      if (!original) {
        throw new Error(`Strategy ${id} not found`);
      }

      // Step 2: Create new strategy with copied data
      // Note: id=undefined lets create() generate new ID and timestamps
      const clonedStrategy = await this.create({
        ...original,
        name: `${original.name} (Copy)`,
        id: undefined,
      });

      console.log(`[StrategyService] Cloned strategy: ${original.id} -> ${clonedStrategy.id}`);

      // Step 3: Get all blocks from original
      const originalBlocksService = this.getBlocksService(id);
      const originalBlocks = await originalBlocksService.list({ limit: 1000 });

      if (originalBlocks.length === 0) {
        console.log(`[StrategyService] No blocks to clone`);
        return clonedStrategy;
      }

      console.log(`[StrategyService] Cloning ${originalBlocks.length} blocks...`);

      // Step 4: Clone each block with its screens
      const clonedBlocksService = this.getBlocksService(clonedStrategy.id!);

      for (const originalBlock of originalBlocks) {
        // Clone block with new entityId and cleared edges
        const clonedBlock = await clonedBlocksService.create({
          ...originalBlock,
          name: `${originalBlock.name} (Copy)`,
          id: undefined,
          entityId: crypto.randomUUID(), // Generate new semantic UUID for linking
          action: {
            ...originalBlock.action,
            edges: [], // Clear all edges - NO transitions cloned
          },
        });

        console.log(`[StrategyService] Cloned block: ${originalBlock.id} -> ${clonedBlock.id} (entityId: ${clonedBlock.entityId})`);

        // Get all V2 StepNode screens from original block
        const originalScreensService = new StrategyStepNodeService(this.projectId, id, originalBlock.id!);
        const originalScreens = await originalScreensService.listScreens();

        if (originalScreens.length === 0) {
          console.log(`[StrategyService] No screens to clone for block ${clonedBlock.id}`);
          continue; // Skip if block has no screens
        }

        console.log(`[StrategyService] Cloning ${originalScreens.length} screens for block ${clonedBlock.id}...`);

        // Clone each V2 StepNode screen
        const clonedScreensService = new StrategyStepNodeService(this.projectId, clonedStrategy.id!, clonedBlock.id!);

        for (const originalScreen of originalScreens) {
          // Clone screen AS IS - NO id generation, NO edge clearing, NO name changes.
          // Screen IDs are scoped to the cloned block and internal edges depend on them.
          await clonedScreensService.addScreen({
            ...originalScreen,
          });

          console.log(`[StrategyService] Cloned StepNode screen: ${originalScreen.id}`);
        }

        console.log(`[StrategyService] ✅ Completed cloning ${originalScreens.length} screens for block ${clonedBlock.id}`);
      }

      console.log(`[StrategyService] Clone complete: ${clonedStrategy.id}`);

      return clonedStrategy;
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'StrategyService.cloneStrategy');
      throw error;
    }
  }

  /**
   * Clone a single block with all its screens
   *
   * Used by BlockFlowCoordinator for duplicate button functionality.
   * Clones block with:
   * - New entityId (crypto.randomUUID()) for BLOCK only
   * - Cleared edges (action.edges = []) for BLOCK only
   * - Name appended with " (Copy)" for BLOCK only
   * - Screens cloned AS IS (no entityId generation, no edge clearing, no name changes)
   *
   * @param strategyId - Strategy ID containing the block
   * @param sourceBlockId - Firestore document ID of block to clone
   * @returns Promise resolving with cloned block (includes all properties from Firebase)
   */
  async cloneBlock(strategyId: string, sourceBlockId: string): Promise<any> {
    try {
      console.log(`[StrategyService] Starting block clone: ${sourceBlockId} in strategy ${strategyId}`);

      // Get blocks service for this strategy
      const blocksService = this.getBlocksService(strategyId);

      // Get original block
      const originalBlock = await blocksService.get(sourceBlockId);
      if (!originalBlock) {
        throw new Error(`Block ${sourceBlockId} not found`);
      }

      // Clone block with new entityId and cleared edges
      const clonedBlock = await blocksService.create({
        ...originalBlock,
        name: `${originalBlock.name} (Copy)`, // Add (Copy) to block name
        id: undefined,
        entityId: crypto.randomUUID(), // Generate new semantic UUID for linking
        action: {
          ...originalBlock.action,
          edges: [], // Clear all edges - NO transitions cloned
        },
      });

      console.log(`[StrategyService] Cloned block: ${originalBlock.id} -> ${clonedBlock.id} (entityId: ${clonedBlock.entityId})`);

      // Get all V2 StepNode screens from original block.
      // Use Firestore ID (not entityId) for Firebase subcollection paths.
      console.log(`[StrategyService] Creating StrategyStepNodeService with:`, {
        projectId: this.projectId,
        strategyId: strategyId,
        blockId: originalBlock.id  // Use Firestore ID for screen path
      });
      const originalScreensService = new StrategyStepNodeService(this.projectId, strategyId, originalBlock.id!);
      console.log(`[StrategyService] Fetching screens from Firebase path: projects/${this.projectId}/strategies/${strategyId}/blocks/${originalBlock.id}/screens`);
      const originalScreens = await originalScreensService.listScreens();
      console.log(`[StrategyService] Firebase returned ${originalScreens.length} screens:`, originalScreens.map((s: any) => ({ id: s.id, name: s.name })));

      if (originalScreens.length === 0) {
        console.log(`[StrategyService] No screens to clone for block ${clonedBlock.entityId}`);
        console.log(`[StrategyService] ✅ Block clone complete: ${clonedBlock.id} (entityId: ${clonedBlock.entityId})`);
        return clonedBlock;
      }

      console.log(`[StrategyService] Cloning ${originalScreens.length} screens for block ${clonedBlock.id} (entityId: ${clonedBlock.entityId})...`);

      // Clone each V2 StepNode screen - use Firestore ID for screen path.
      const clonedScreensService = new StrategyStepNodeService(this.projectId, strategyId, clonedBlock.id!);

      for (const originalScreen of originalScreens) {
        // Clone screen AS IS - NO id generation, NO edge clearing, NO name changes.
        // Screen IDs are scoped to the cloned block and internal edges depend on them.
        await clonedScreensService.addScreen({
          ...originalScreen,
        });

        console.log(`[StrategyService] Cloned StepNode screen: ${originalScreen.id} (name: ${originalScreen.name})`);
      }

      console.log(`[StrategyService] ✅ Block clone complete: ${clonedBlock.id} (${originalScreens.length} screens cloned)`);

      return clonedBlock;
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'StrategyService.cloneBlock');
      throw error;
    }
  }

  /**
   * Get StrategyBlockService for a strategy's blocks subcollection
   *
   * @param strategyId - Strategy ID
   * @returns {StrategyBlockService} StrategyBlockService instance scoped to this strategy
   */
  getBlocksService(strategyId: string): any {
    // StrategyBlockService imported at top of file (static ES import)
    return new StrategyBlockService(this.projectId, strategyId);
  }

  async listAllStepNodeScreens(strategyId: string): Promise<StrategyStepNodeScreenTarget[]> {
    try {
      const blockService = new StrategyBlockService(this.projectId, strategyId);
      const blocks = await blockService.list({ limit: 500 });
      const targets: StrategyStepNodeScreenTarget[] = [];

      for (const block of blocks) {
        const blockId = block.id;
        if (!blockId) continue;
        const stepNodeService = new StrategyStepNodeService(this.projectId, strategyId, blockId);
        const screens = await stepNodeService.listScreens();
        for (const screen of screens) {
          targets.push({
            blockId,
            blockName: block.name,
            screenId: screen.id,
            screen,
          });
        }
      }

      return targets;
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'StrategyService.listAllStepNodeScreens');
      throw error;
    }
  }

  async replaceStepNodeScreen(
    strategyId: string,
    blockId: string,
    screenId: string,
    screen: StepNode
  ): Promise<void> {
    try {
      const stepNodeService = new StrategyStepNodeService(this.projectId, strategyId, blockId);
      const latest = await stepNodeService.getScreen(screenId);
      const latestAiAssets = (latest as any)?.aiAssets;
      const nextScreen = latestAiAssets && typeof latestAiAssets === 'object'
        ? ({ ...(screen as any), aiAssets: latestAiAssets } as StepNode)
        : screen;
      await stepNodeService.replaceScreen(screenId, nextScreen);
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'StrategyService.replaceStepNodeScreen');
      throw error;
    }
  }

  // ============================================================================
  // THEME SETTINGS V2 (subcollection: strategies/{sid}/themeSettingsV2/settings)
  // ============================================================================

  private readonly themeSettingsV2CollectionName = 'themeSettingsV2';
  private readonly themeSettingsDocId = 'settings';

  /**
   * Get V2 theme settings for a strategy.
   * Returns null if no theme is attached.
   */
  async getThemeSettingsV2(
    strategyId: string
  ): Promise<OnboardingThemeSettingsV2 | null> {
    try {
      const docRef = doc(
        this.firestore,
        `projects/${this.projectId}/strategies/${strategyId}/${this.themeSettingsV2CollectionName}`,
        this.themeSettingsDocId
      );
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) return null;
      return docSnap.data() as OnboardingThemeSettingsV2;
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'StrategyService.getThemeSettingsV2');
      throw error;
    }
  }

  /**
   * Save V2 theme settings to strategy subcollection.
   * Creates or replaces the theme settings document.
   */
  async saveThemeSettingsV2(
    strategyId: string,
    themeSettings: OnboardingThemeSettingsV2
  ): Promise<void> {
    try {
      const docRef = doc(
        this.firestore,
        `projects/${this.projectId}/strategies/${strategyId}/${this.themeSettingsV2CollectionName}`,
        this.themeSettingsDocId
      );
      await setDoc(docRef, stripUndefinedDeep(themeSettings));

      // Update parent strategy's updatedAt
      const parentRef = doc(
        this.firestore,
        `projects/${this.projectId}/strategies`,
        strategyId
      );
      await setDoc(parentRef, { updatedAt: new Date() }, { merge: true });
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'StrategyService.saveThemeSettingsV2');
      throw error;
    }
  }

  /**
   * Ensure a strategy owns a full V2 theme snapshot.
   *
   * Strategy screens are merged from the strategy-local theme template, not from
   * the project theme directly. New AI strategies must therefore snapshot the
   * active project theme before screen generation starts.
   */
  async ensureThemeSettingsV2FromActiveProjectTheme(
    strategyId: string,
    options: { required?: boolean } = {}
  ): Promise<OnboardingThemeSettingsV2 | null> {
    const existing = await this.getThemeSettingsV2(strategyId);
    if (existing) {
      return existing;
    }

    const projectThemeService = new ProjectThemeService();
    const activeTheme = await projectThemeService.getActive(this.projectId);
    if (!activeTheme) {
      if (options.required) {
        throw new Error('Strategy screen generation requires an active project theme, but no active project theme is configured.');
      }
      return null;
    }

    const snapshot = cloneOnboardingThemeSettings(activeTheme, {
      sourceThemeId: activeTheme.sourceThemeId ?? activeTheme.id,
    });
    await this.saveThemeSettingsV2(strategyId, snapshot);
    return snapshot;
  }

  /**
   * Delete V2 theme settings from strategy.
   */
  async deleteThemeSettingsV2(strategyId: string): Promise<void> {
    try {
      const docRef = doc(
        this.firestore,
        `projects/${this.projectId}/strategies/${strategyId}/${this.themeSettingsV2CollectionName}`,
        this.themeSettingsDocId
      );
      await deleteDoc(docRef);
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'StrategyService.deleteThemeSettingsV2');
    }
  }
}

// ============================================================================
// TEST UTILITIES
// ============================================================================

/**
 * Create a mock StrategyService for testing
 *
 * Usage in tests:
 * ```typescript
 * import { createMockStrategyService } from '@modules/firebase-core/services/StrategyService';
 *
 * const mockService = createMockStrategyService({
 *   list: jest.fn().mockResolvedValue([...strategies]),
 *   create: jest.fn().mockResolvedValue(newStrategy),
 * });
 * ```
 *
 * @param overrides - Optional method overrides
 * @returns Mocked StrategyService instance
 */
export function createMockStrategyService(
  overrides: Partial<jest.Mocked<StrategyService>> = {}
): jest.Mocked<StrategyService> {
  const unsubscribeMock = jest.fn();

  return {
    // CRUD operations
    list: jest.fn().mockResolvedValue([]),
    get: jest.fn().mockResolvedValue(null),
    create: jest.fn().mockResolvedValue({ id: 'new-strategy-id' }),
    update: jest.fn().mockResolvedValue({ id: 'updated-strategy-id' }),
    delete: jest.fn().mockResolvedValue(undefined),
    findByName: jest.fn().mockResolvedValue(null),

    // Advanced operations
    cloneStrategy: jest.fn().mockResolvedValue({ id: 'cloned-strategy-id' }),

    // Real-time subscriptions
    onSnapshot: jest.fn().mockReturnValue(unsubscribeMock),
    onCollectionSnapshot: jest.fn((onNext) => {
      // Simulate immediate data
      setTimeout(() => onNext([]), 0);
      return unsubscribeMock;
    }),

    // Pagination
    listWithPagination: jest.fn().mockResolvedValue({
      strategies: [],
      hasMore: false,
      lastDoc: null,
    }),

    // Subcollection access
    getBlocksService: jest.fn().mockReturnValue({}),

    // Allow overrides
    ...overrides,
  } as unknown as jest.Mocked<StrategyService>;
}
